import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const ScrollToTopLink: React.FC<{ to: string; children: React.ReactNode; className?: string; onClick?: () => void; style?: React.CSSProperties }> = ({ to, children, className, onClick, style }) => {
  const handleClick = () => {
    window.scrollTo(0, 0);
    if (onClick) onClick();
  };

  return (
    <Link to={to} className={className} onClick={handleClick} style={style}>
      {children}
    </Link>
  );
};

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Centralized styling configuration
  const styles = {
    navbar: {
      shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      zIndex: {
        header: 50,
        backdrop: 45,
        menu: 50
      }
    },
    button: {
      fontSize: '1.2rem',
      backgroundColor: '#f3f4f6',
      color: '#1f2937',
      boxShadow: '0 .5px 1.5px 0 rgba(0, 0, 0, 0.1)',
      borderRadius: '0.5rem',
      width: '2.3rem',
      height: '2rem'
    },
    menu: {
      top: '4.375rem',
      backgroundColor: 'white',
      boxShadow: '0 5px 7.5px -1.5px rgba(0, 0, 0, 0.1)',
      borderTop: '.5px solid #e5e7eb',
      spacerHeight: '4.375rem'
    },
    backdrop: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)'
    }
  };

  const navItems = [
    { name: 'Welcome', path: '/' },
    { name: 'Schedule', path: '/schedule' },
    { name: 'Travel', path: '/travel' },
    { name: 'Accommodation', path: '/accommodation' },
    { name: 'Gifts', path: '/gifts' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:flex fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm px-6 py-4" style={{ boxShadow: styles.navbar.shadow, zIndex: styles.navbar.zIndex.header }}>
        <div className="max-w-6xl mx-auto w-full flex items-center justify-between">
          <ScrollToTopLink to="/" className="script-heading text-gold">
            Cat & Rich
          </ScrollToTopLink>
          
          <div className="flex space-x-8">
            {navItems.map((item) => (
              <ScrollToTopLink
                key={item.path}
                to={item.path}
                className={`nav-text transition-colors duration-200 hover-text-gold ${
                  location.pathname === item.path ? 'text-gold' : 'text-charcoal'
                }`}
              >
                {item.name}
              </ScrollToTopLink>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <div className="md:hidden">
        {/* Mobile Header */}
        <div className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm px-4 py-3" style={{ boxShadow: styles.navbar.shadow, zIndex: styles.navbar.zIndex.header }}>
          <div className="flex items-center justify-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="transition-colors"
              style={{
                ...styles.button,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: styles.backdrop.backgroundColor,
              zIndex: styles.navbar.zIndex.backdrop
            }}
            onClick={() => setIsOpen(false)}
          />
        )}

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              style={{
                position: 'fixed',
                top: styles.menu.top,
                left: 0,
                right: 0,
                backgroundColor: styles.menu.backgroundColor,
                boxShadow: styles.menu.boxShadow,
                borderTop: styles.menu.borderTop,
                zIndex: styles.navbar.zIndex.menu
              }}
            >
              <div className="px-8 py-6">
                {navItems.map((item) => (
                  <ScrollToTopLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block body-text text-lg text-center transition-colors duration-200 hover-text-gold no-underline ${
                      location.pathname === item.path ? 'text-gold' : 'text-charcoal'
                    }`}
                    style={{
                      textDecoration: 'none',
                      padding: '1rem 0'
                    }}
                  >
                    {item.name}
                  </ScrollToTopLink>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Spacer for fixed navigation */}
      <div className="h-16 md:h-20" style={{ height: styles.menu.spacerHeight }}></div>
    </>
  );
};

export default Navigation;